import React, { useEffect, useRef } from "react"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@coval/ui"
import { AlertTriangle, Download, Pencil, Upload } from "lucide-react"

import { logger } from "@/lib/logger"
import { ResourceHeaderActions } from "@/components/resource-header/ResourceHeaderActions"

const log = logger("DashboardActions")

interface DashboardActionsProps {
  onClearDashboard: () => void
  onDeleteDashboard: () => void | Promise<void>
  onExportDashboard: () => void
  onImportDashboard: (file: File) => Promise<void>
  onRenameDashboard?: (newName: string) => Promise<void>
  widgetCount: number
  isDefaultDashboard: boolean
  dashboardName: string
  dashboardId: string
  dashboardUrl: string
  createdAt?: string | null
  updatedAt?: string | null
}

export const DashboardActions: React.FC<DashboardActionsProps> = ({
  onDeleteDashboard,
  onExportDashboard,
  onImportDashboard,
  onRenameDashboard,
  widgetCount,
  isDefaultDashboard,
  dashboardName,
  dashboardId,
  dashboardUrl,
  createdAt,
  updatedAt,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const renameInputId = React.useId()

  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false)
  const [isRenameOpen, setIsRenameOpen] = React.useState(false)
  const [renameValue, setRenameValue] = React.useState(dashboardName)
  const [isDeleting, setIsDeleting] = React.useState(false)
  const [isRenaming, setIsRenaming] = React.useState(false)

  useEffect(() => {
    if (isRenameOpen) {
      setRenameValue(dashboardName)
    }
  }, [isRenameOpen, dashboardName])

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (file) {
      try {
        await onImportDashboard(file)
      } catch (error) {
        log.error("Import failed", error)
      }
    }
    event.target.value = ""
  }

  const handleDeleteConfirm = async () => {
    setIsDeleting(true)
    try {
      await onDeleteDashboard()
      setIsDeleteOpen(false)
    } catch (error) {
      toast.error({
        title: "Could not delete dashboard",
        error,
        fallback: "Could not delete the dashboard.",
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRenameConfirm = async () => {
    const trimmed = renameValue.trim()
    if (trimmed && onRenameDashboard && trimmed !== dashboardName) {
      setIsRenaming(true)
      try {
        await onRenameDashboard(trimmed)
        setIsRenameOpen(false)
      } catch (error) {
        toast.error({
          title: "Could not rename dashboard",
          error,
          fallback: "Could not rename the dashboard.",
        })
      } finally {
        setIsRenaming(false)
      }
    }
  }

  return (
    <>
      <ResourceHeaderActions
        additionalActions={[
          ...(onRenameDashboard && !isDefaultDashboard
            ? [
                {
                  label: "Rename dashboard",
                  onSelect: () => setIsRenameOpen(true),
                  icon: <Pencil />,
                  "data-testid": "dashboard-rename-button",
                },
              ]
            : []),
          {
            label: "Export dashboard",
            onSelect: onExportDashboard,
            disabled: widgetCount === 0,
            icon: <Download />,
          },
          {
            label: "Import dashboard",
            onSelect: handleImportClick,
            icon: <Upload />,
          },
        ]}
        resourceId={dashboardId}
        resourceUrl={dashboardUrl}
        attribution={{ createdAt, updatedAt }}
        delete={{
          label: "Delete dashboard",
          onDelete: () => setIsDeleteOpen(true),
          pending: isDeleting,
          disabled: isDefaultDashboard,
          "data-testid": "dashboard-delete-button",
        }}
        menuLabel="Dashboard actions"
        menuTestId="dashboard-actions-menu"
      />

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={(event) => {
          void handleFileChange(event)
        }}
        style={{ display: "none" }}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={isDeleteOpen}
        onOpenChange={(open) => {
          if (!open && isDeleting) {
            return
          }
          setIsDeleteOpen(open)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-coval-danger-text" />
              Delete Dashboard
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete the{" "}
              <strong>"{dashboardName}"</strong> dashboard? This action cannot
              be undone. All widgets and configurations will be permanently
              removed.
              {widgetCount > 0 && (
                <>
                  {" "}
                  This dashboard contains {widgetCount} widget
                  {widgetCount !== 1 ? "s" : ""}.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="ghost" disabled={isDeleting}>
                Cancel
              </Button>
            </AlertDialogCancel>
            <Button
              variant="danger"
              onClick={() => void handleDeleteConfirm()}
              disabled={isDeleting}
              data-testid="dashboard-delete-confirm-button"
            >
              Delete Dashboard
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rename dashboard dialog */}
      <Dialog
        open={isRenameOpen}
        onOpenChange={(open) => {
          if (!open && isRenaming) {
            return
          }
          setIsRenameOpen(open)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename dashboard</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={renameInputId}>Dashboard name</Label>
              <Input
                id={renameInputId}
                aria-label="Dashboard name"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                maxLength={64}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    void handleRenameConfirm()
                  }
                }}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                if (!isRenaming) {
                  setIsRenameOpen(false)
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={() => void handleRenameConfirm()}
              disabled={
                !renameValue.trim() ||
                renameValue.trim() === dashboardName ||
                isRenaming
              }
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
